import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Calendar, Clock, CheckCircle, ArrowRight, Sparkles, Shield, Star } from 'lucide-react';
import './QuickBooking.css';

const QuickBooking = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: Search,
      title: 'Search',
      description: 'Find doctors by symptoms or specialty',
      features: ['AI-powered matching', 'Instant results', 'Filter by location'],
      color: 'var(--primary-teal)',
      delay: '0s'
    },
    {
      number: 2,
      icon: User,
      title: 'Select',
      description: 'Choose your preferred doctor and time',
      features: ['View doctor profiles', 'Check availability', 'Read reviews'],
      color: 'var(--info-coral)',
      delay: '0.2s'
    },
    {
      number: 3,
      icon: Calendar,
      title: 'Book',
      description: 'Confirm your appointment instantly',
      features: ['Instant confirmation', 'SMS reminders', 'Easy rescheduling'],
      color: 'var(--success-lime)',
      delay: '0.4s'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Book in 60 seconds',
      description: 'Quick and easy appointment booking'
    },
    {
      icon: Shield,
      title: 'Verified doctors',
      description: 'All doctors are verified and licensed'
    },
    {
      icon: Star,
      title: '4.9/5 rating',
      description: 'Highly rated by our patients'
    }
  ];

  return (
    <section className="quick-booking">
      <div className="container">
        {/* Header */}
        <div className="booking-header">
          <div className="booking-badge">
            <Sparkles size={16} />
            <span>Quick & Easy</span>
          </div>
          <h2>Book Your Appointment in 3 Simple Steps</h2>
          <p>Get the care you need with our streamlined booking process</p>
        </div>

        {/* Steps */}
        <div className="booking-steps">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div 
                key={index} 
                className="booking-step"
                style={{ '--step-color': step.color, '--delay': step.delay }}
              >
                <div className="step-header">
                  <div className="step-number" style={{ backgroundColor: step.color }}>
                    {step.number}
                  </div>
                  <div className="step-icon" style={{ color: step.color }}>
                    <IconComponent size={32} />
                  </div>
                </div>
                
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                  
                  <ul className="step-features">
                    {step.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="step-feature">
                        <CheckCircle size={16} className="feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div className="step-connector">
                    <div className="connector-line"></div>
                    <ArrowRight size={20} className="connector-arrow" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="booking-benefits">
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="benefit-item">
                  <div className="benefit-icon">
                    <IconComponent size={24} />
                  </div>
                  <div className="benefit-content">
                    <h4 className="benefit-title">{benefit.title}</h4>
                    <p className="benefit-description">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="booking-cta">
          <div className="cta-content">
            <div className="cta-text">
              <h3>Ready to Get Started?</h3>
              <p>Join thousands of patients who trust DoctDash for their healthcare needs</p>
            </div>
            <div className="cta-buttons">
              <button 
                onClick={() => navigate('/find-doctors')}
                className="cta-btn cta-btn-primary"
              >
                <Search size={18} />
                <span>Find Doctors</span>
                <ArrowRight size={16} />
              </button>
              <button 
                onClick={() => navigate('/book-appointment')}
                className="cta-btn cta-btn-secondary"
              >
                <Calendar size={18} />
                <span>Book Now</span>
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="cta-stats">
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Appointments Booked</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2.5K+</div>
              <div className="stat-label">Doctors Available</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99.9%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickBooking;